import { createSelector } from '@reduxjs/toolkit';
import {
  getHasLinkedWorkItemsSupport,
  getHasTicIntegrationsSupport,
} from 'redux/selectors/accounts';
import { getTicApiSlice } from './api-slice/tic.api.selector';
import { ticApi } from './api-slice/tic.apiSlice';
import { getContextualTicAccounts, getHasTicWorkspace } from './tic.auth.selectors';

/**
 * Check if any connection (account or personal) has reference_tasks enabled.
 * Used to determine if task picker should be shown in HourEditor (TT-4242).
 *
 * Returns false if:
 * - No accountId available
 * - Integrations data not loaded
 * - No connections have reference_tasks: true
 */
export const getHasAnyConnectionWithReferenceTasks = createSelector(
  [getTicApiSlice, getContextualTicAccounts],
  (apiState, { workspace, personalSpace }): boolean => {
    const accountId = workspace?.id || personalSpace?.id;
    if (!accountId) return false;

    const integrationsResult = ticApi.endpoints.getIntegrations.select({ accountId })({
      [ticApi.reducerPath]: apiState,
    });

    const integrations = integrationsResult.data?.data || [];

    return integrations.some(integration => {
      const accountConnection = integration.account?.connection;
      const personalConnection = integration.personal?.connection;

      return (
        accountConnection?.settings?.reference_tasks === true ||
        personalConnection?.settings?.reference_tasks === true
      );
    });
  }
);

/**
 * Whether the linked-work surface should exist at all: the account has the TIC integrations
 * feature, a TIC workspace maps to it, AND some connection actually references tasks.
 *
 * The workspace conjunct is not redundant. `getHasAnyConnectionWithReferenceTasks` falls back to
 * the personal space when no workspace maps to the account, but every task query keys off
 * `workspace?.id` and skips without one — so on a personal-space-only tenant the capability would
 * go true and each surface would render a picker that can never load a row, reading "no linked work
 * items found" forever. Personal-space task queries are a separate piece of work.
 *
 * Deliberately has no access to the fetched task pool. Every surface this gates either IS the
 * linked-work picker or renders it, and the picker owns the only UI for the status filters that
 * decide whether the pool holds anything — so a gate read off the pool can be switched off by a
 * filter and then hide the control that clears that filter (TT-5457). Fetched results decide what
 * is INSIDE the picker; only the account decides whether the picker exists.
 *
 * Both inputs are false while their fetches are in flight or after they fail, so this is false
 * until `getIntegrations` succeeds — the same trade `HourEditor` already makes with this pair.
 */
export const getHasLinkedWorkCapability = createSelector(
  [getHasTicIntegrationsSupport, getHasTicWorkspace, getHasAnyConnectionWithReferenceTasks],
  (hasSupport, hasWorkspace, hasConnectionWithReferenceTasks) =>
    hasSupport && hasWorkspace && hasConnectionWithReferenceTasks
);

/**
 * Whether a linked-work PICKER should be rendered: either source can supply rows on its own.
 *
 * Two independent sources feed the same picker. TIC tasks need the integrations feature, a mapped
 * workspace and a connection that references tasks (`getHasLinkedWorkCapability`). Native
 * (Rails-backed) linked work items need none of that — they are account rows behind the
 * `api_linked_work` feature and load with no TIC session at all. An account can have either, both,
 * or neither, so the picker exists when EITHER holds.
 *
 * Kept separate from `getHasLinkedWorkCapability` rather than folded into it: that selector answers
 * "does this account have TIC linked work", which several TIC-only decisions still need. This one
 * answers "is there anything to pick", and is what every picker surface gates on.
 */
export const getHasLinkedWorkPicker = createSelector(
  [getHasLinkedWorkCapability, getHasLinkedWorkItemsSupport],
  (hasTicLinkedWork, hasNativeLinkedWork) => hasTicLinkedWork || hasNativeLinkedWork
);

/**
 * Get list of connected TIC integrations that have reference_tasks enabled.
 * Used for filtering hours by linked work items (TT-4385).
 *
 * Returns empty array if:
 * - No accountId available
 * - Integrations data not loaded
 * - No connections have reference_tasks: true
 *
 * Maps TIC provider IDs to legacy app IDs:
 * - 'asana' -> 'asana_sync'
 */
export const getConnectedTicIntegrationsWithReferenceTasks = createSelector(
  [getTicApiSlice, getContextualTicAccounts],
  (apiState, { workspace, personalSpace }): Array<string> => {
    const accountId = workspace?.id || personalSpace?.id;
    if (!accountId) return [];

    const integrationsResult = ticApi.endpoints.getIntegrations.select({ accountId })({
      [ticApi.reducerPath]: apiState,
    });

    const integrations = integrationsResult.data?.data || [];

    const connectedIntegrations = integrations
      .filter(integration => {
        const accountConnection = integration.account?.connection;
        const personalConnection = integration.personal?.connection;

        return (
          accountConnection?.settings?.reference_tasks === true ||
          personalConnection?.settings?.reference_tasks === true
        );
      })
      .map(integration => integration.id);

    return connectedIntegrations;
  }
);

/**
 * Get icon URLs for TIC integrations that have reference_tasks enabled.
 * Returns a map of integration ID -> icon URL.
 *
 * Used to display provider logos in the linked work filter dropdown.
 */
export const getTicIntegrationIcons = createSelector(
  [getTicApiSlice, getContextualTicAccounts],
  (apiState, { workspace, personalSpace }): Record<string, string | null> => {
    const accountId = workspace?.id || personalSpace?.id;
    if (!accountId) return {};

    const integrationsResult = ticApi.endpoints.getIntegrations.select({ accountId })({
      [ticApi.reducerPath]: apiState,
    });

    const integrations = integrationsResult.data?.data || [];

    const iconMap: Record<string, string | null> = {};

    integrations.forEach(integration => {
      const accountConnection = integration.account?.connection;
      const personalConnection = integration.personal?.connection;

      if (
        accountConnection?.settings?.reference_tasks === true ||
        personalConnection?.settings?.reference_tasks === true
      ) {
        iconMap[integration.id] = integration.icon_url;
      }
    });

    return iconMap;
  }
);
